import {
  Autocomplete,
  type AutocompleteChangeReason,
  CircularProgress,
  ClickAwayListener,
  debounce,
  InputAdornment,
  MenuItem,
  Stack,
  type SxProps,
  TextField,
  type Theme,
} from '@mui/material';
import { getSmartyAddress } from 'api/smarty/smarty.requests';
import type { SmartyAutoCompleteResponse } from 'api/smarty/smarty.types';
import type { AxiosError } from 'axios';
import { defaultDebounceDelay, STATE_LIST, ZIPCODE_LENGTH } from 'common/constants';
import { FormControl } from 'components/lib/FormControl';
import { Select } from 'components/lib/Select';
import type { SmartyAutoCompleteValues } from 'components/lib/SmartyAutocompleteInput/SmartyAutocompleteInput.types';
import { formatSelectedSmartyOptionWithEntries } from 'components/lib/SmartyAutocompleteInput/SmartyAutocompleteInput.utils';
import { useFormik } from 'formik';
import {
  formatLocation,
  parseInitialAddressForManualAddressInput,
  parseLocation,
} from 'helpers/utils';
import { isEmpty, isObject, isString, map, rest } from 'lodash-es';
import {
  forwardRef,
  type SyntheticEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import type { IContext } from 'types/IContext';
import * as yup from 'yup';
import type { ManualAddressInputProps, ManualAddressInputRef } from './ManualAddressInput.types';

const initialSmartyAddressAutocompleteData = {
  data: [] as SmartyAutoCompleteResponse[],
  loading: false,
  loaded: false,
};

let smartyAddressRequestController: AbortController;

const ManualAddressInput = forwardRef<ManualAddressInputRef | undefined, ManualAddressInputProps>(
  (
    {
      isReadOnly,
      initialAddress,
      includedStates,
      preferStates,
      useAutoCompleteAddressInput,
      useRegularParsingLogicForInitialAddress,
      updateAddressField,
      addressLineMaxLength,
    },
    ref,
  ) => {
    const [isSelectOpen, setIsSelectOpen] = useState(false);

    const [options, setOptions] = useState<IContext<SmartyAutoCompleteResponse[]>>(
      initialSmartyAddressAutocompleteData,
    );

    const [state, setState] = useState<SmartyAutoCompleteValues>(() =>
      initialAddress
        ? useRegularParsingLogicForInitialAddress
          ? parseLocation(initialAddress)
          : parseInitialAddressForManualAddressInput(initialAddress)
        : {
            street_line: '',
            secondary: '',
            city: '',
            state: '',
            zipcode: '',
          },
    );

    const validationSchema = yup.object({
      street_line: isReadOnly ? yup.string() : yup.string().required('Required'),
      secondary: yup.string(),
      city: isReadOnly ? yup.string() : yup.string().required('Required'),
      state: isReadOnly ? yup.string() : yup.string().required('Required'),
      zipcode: isReadOnly
        ? yup.string()
        : yup
            .string()
            .required('Required')
            .matches(/^\d*$/, 'Only digits allowed')
            .length(ZIPCODE_LENGTH, `Please enter a valid ${ZIPCODE_LENGTH}-digit Zip Code`),
    });

    const formik = useFormik<SmartyAutoCompleteValues>({
      initialValues: {
        ...state,
      },
      validationSchema,
      onSubmit: async () => {},
    });

    const validateForm = async () => {
      await formik.submitForm();
      const errors = await formik.validateForm();

      if (!isEmpty(errors)) {
        await formik.setTouched(
          {
            ...formik.touched,
            ...Object.keys(errors).reduce((acc, key) => {
              acc[key] = true;

              return acc;
            }, {}),
          },
          false,
        );

        return false;
      }

      return true;
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      if (state?.[name] !== value) {
        formik.handleBlur(e);

        updateState(value, name);
      }
    };

    useImperativeHandle(ref, () => ({
      validate: async () => validateForm(),
    }));

    const getAddressSuggestion = async (search?: string, selected?: string) => {
      try {
        smartyAddressRequestController?.abort();
        smartyAddressRequestController = new AbortController();

        setOptions((prevState) => ({
          ...prevState,
          loading: true,
          loaded: false,
        }));

        const smartyResponse = await getSmartyAddress(
          {
            search,
            selected,
            source: 'all',
            max_results: 10,
            prefer_ratio: 50,
            ...(!isEmpty(includedStates) ? { state_filter: includedStates } : {}),
            ...(!isEmpty(preferStates) ? { prefer_states: preferStates } : {}),
          },
          smartyAddressRequestController,
        );

        setOptions((prevState) => ({
          ...prevState,
          loading: false,
          loaded: true,
          data: smartyResponse,
        }));

        return smartyResponse;
      } catch (error) {
        const e = error as AxiosError;

        if (e?.message !== 'canceled') {
          setOptions({ ...options, loading: false, loaded: true });
          throw e;
        }

        return null;
      }
    };

    const handleSearch = (event: SyntheticEvent, newValue: string) => {
      if (event?.type === 'change') {
        if (newValue) {
          setIsSelectOpen(true);
          getAddressSuggestion(newValue);
        }
      }
    };

    const updateState = (address: SmartyAutoCompleteResponse | string, fieldName?: string) => {
      if (isObject(address)) {
        setState((prevState) => ({
          ...prevState,
          ...address,
        }));

        formik.setValues(address as SmartyAutoCompleteValues);
      }

      if (fieldName) {
        setState((prevState) => ({
          ...prevState,
          [fieldName]: address,
        }));

        formik.setFieldValue(fieldName, address);
      }
    };

    const handleAutoCompleteOnChange = (
      e: SyntheticEvent,
      selectedOption: SmartyAutoCompleteResponse | string,
      reason: AutocompleteChangeReason,
    ) => {
      if (reason === 'selectOption') {
        e.preventDefault();

        if (isObject(selectedOption)) {
          if (selectedOption.entries! > 1) {
            getAddressSuggestion(
              `${selectedOption?.street_line} ${selectedOption?.secondary}`,
              formatSelectedSmartyOptionWithEntries(selectedOption, true),
            );
            setIsSelectOpen(true);
          } else {
            setIsSelectOpen(false);
            updateState(selectedOption);
          }
        } else {
          setIsSelectOpen(false);
          updateState(selectedOption);
        }
      }
    };

    const errorFieldParser = (name: string, useErrorMessage?: boolean, useOnlyError?: boolean) => {
      if (isReadOnly) {
        return {
          error: false,
          ...(useErrorMessage
            ? {
                errorMessage: '',
              }
            : {
                helperText: '',
              }),
        };
      }

      if (useOnlyError) {
        return {
          error: formik?.touched?.[name] && Boolean(formik.errors?.[name]),
        };
      }
      return {
        error: formik?.touched[name] && Boolean(formik.errors[name]),
        ...(useErrorMessage
          ? {
              errorMessage: formik?.touched[name] && formik?.errors?.[name],
            }
          : {
              helperText: formik?.touched[name] && formik?.errors[name],
            }),
      };
    };

    const renderTextField = ({
      name,
      label,
      sx,
      maxLength,
    }: {
      name: string;
      label: string;
      sx?: SxProps<Theme>;
      maxLength?: number;
    }) => {
      const fieldValue = formik.values?.[name];

      const isZipInput = name === 'zipcode';

      return (
        <TextField
          name={name}
          label={label}
          value={fieldValue}
          disabled={isReadOnly}
          autoComplete="new-password"
          sx={sx}
          {...errorFieldParser(name)}
          onChange={(e) => {
            if (isZipInput) {
              const { value } = e.target;

              if (!/^\d*$/.test(value)) return;
            }

            formik.handleChange(e);
          }}
          onBlur={handleBlur}
          slotProps={{
            htmlInput: {
              'data-test': name,
              inputMode: isZipInput ? 'numeric' : 'text',
              maxLength,
            },
          }}
        />
      );
    };

    const renderOptionLabel = (option: SmartyAutoCompleteResponse | string) => {
      if (isString(option)) {
        if (!isEmpty(option)) {
          const location = parseLocation(option);

          return location.street_line;
        } else {
          return option;
        }
      } else if (option.entries! > 1) {
        return `${option?.street_line}`;
      } else {
        const formatted = formatLocation(option);

        return `${formatted.showing.head},${formatted.showing.tail}`;
      }
    };

    const handleClickAway = () => setIsSelectOpen(false);

    const FILTERED_STATES = useMemo(() => {
      if (!isEmpty(includedStates)) {
        return map(includedStates, (includedState) => ({
          code: includedState,
          name: includedState,
        }));
      } else {
        return [];
      }
    }, [includedStates]);

    const STATES_OPTIONS_LIST = !isEmpty(FILTERED_STATES) ? FILTERED_STATES : STATE_LIST;

    const STATES_OPTIONS_LIST_SET = useMemo(
      () => new Set(STATES_OPTIONS_LIST.map((state) => state.code)),
      [STATES_OPTIONS_LIST],
    );

    useEffect(() => {
      const formattedLocation = formatLocation(state).storing;

      updateAddressField(formattedLocation === '-' ? '' : formattedLocation);
    }, [state]);

    useEffect(() => {
      if (formik?.values?.state && !STATES_OPTIONS_LIST_SET.has(formik?.values?.state as string)) {
        updateState('', 'state');
      }
    }, [formik?.values?.state, STATES_OPTIONS_LIST_SET]);

    return (
      <Stack gap={1.4}>
        {useAutoCompleteAddressInput ? (
          <ClickAwayListener onClickAway={handleClickAway}>
            <Stack>
              <Autocomplete
                {...rest}
                open={isSelectOpen}
                disableClearable
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) =>
                  formatLocation(option as SmartyAutoCompleteResponse).storing === value
                }
                freeSolo
                id="street_line"
                onFocus={() => setIsSelectOpen(true)}
                filterOptions={(x) => x}
                autoComplete
                value={formik.values?.street_line || ''}
                readOnly={isReadOnly}
                loading={options.loading}
                options={
                  options?.data?.length! >= 1 && !options.loading ? (options.data ?? []) : []
                }
                onInputChange={debounce(handleSearch, defaultDebounceDelay)}
                onChange={handleAutoCompleteOnChange}
                getOptionLabel={renderOptionLabel}
                onBlur={handleBlur}
                renderInput={(params) => (
                  <form autoComplete="new-password">
                    <TextField
                      {...params}
                      placeholder="Address Line 1"
                      name="street_line"
                      label={isReadOnly ? '' : 'Address Line 1'}
                      disabled={isReadOnly}
                      sx={[(theme) => theme.mixins.placeholderColor(formik, `street_line`)]}
                      {...errorFieldParser(`street_line`)}
                      autoComplete="new-password"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          readOnly: isReadOnly,
                          autoComplete: 'new-password',
                          endAdornment: isReadOnly ? (
                            <></>
                          ) : options.loading ? (
                            <InputAdornment position="end" sx={{ mr: 0.5 }}>
                              <CircularProgress color="inherit" size={20} />
                            </InputAdornment>
                          ) : (
                            <></>
                          ),
                        },

                        htmlInput: {
                          ...params.inputProps,
                          'data-test': `street_line`,
                          maxLength: addressLineMaxLength,
                        },

                        inputLabel: isReadOnly ? { shrink: true } : {},
                      }}
                    />
                  </form>
                )}
                renderOption={(props, opt) => {
                  const option = opt as SmartyAutoCompleteResponse;

                  return (
                    <li {...props} key={props.id}>
                      {`${option.street_line},${option.secondary ? ` ${option.secondary}, ` : ' '} ${
                        (option?.entries as number) < 2 ? '' : `(${option.entries} more entries),`
                      } ${option.city}, ${option.state} ${option.zipcode}`}
                    </li>
                  );
                }}
              />
            </Stack>
          </ClickAwayListener>
        ) : (
          renderTextField({
            name: `street_line`,
            label: 'Address Line 1 ',
            maxLength: addressLineMaxLength,
          })
        )}
        {renderTextField({
          name: `secondary`,
          label: 'Address Line 2 (optional)',
          maxLength: addressLineMaxLength,
        })}
        {renderTextField({ name: `city`, label: 'City' })}
        <Stack direction="row" gap={1.4}>
          <FormControl
            {...errorFieldParser(`state`, true)}
            renderLabel
            field={{
              name: 'State',
              code: `state`,
            }}
            sx={(theme) => ({
              flex: 1,
              '& .MuiInputLabel-root': {
                color: isReadOnly
                  ? theme.palette.customColors.disabledActionBar
                  : theme.palette.grey,
              },
            })}
          >
            <Select
              value={formik?.values?.state}
              label="State"
              testPrefix="State"
              name="state"
              disabled={isReadOnly}
              inputProps={{
                readOnly: isReadOnly,
              }}
              {...errorFieldParser(`state`, false, true)}
              onChange={formik.handleChange}
              preSelectFirstOption={() => {
                if (STATES_OPTIONS_LIST.length === 1) {
                  const firstChoice = STATES_OPTIONS_LIST?.at(0);

                  if (firstChoice) {
                    updateState(firstChoice.code, 'state');
                  }
                }
              }}
              isFieldOptional={false}
              onBlur={handleBlur}
              optionsCount={STATES_OPTIONS_LIST.length}
            >
              {STATES_OPTIONS_LIST.map(({ code, name }) => (
                <MenuItem key={code} value={code} data-test={code}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {renderTextField({
            name: `zipcode`,
            label: 'ZIP Code',
            sx: { flex: 1 },
            maxLength: ZIPCODE_LENGTH,
          })}
        </Stack>
      </Stack>
    );
  },
);

export default ManualAddressInput;
